// -----------------------------------------------------------------------------
// iOS Safari 100vh / bottom toolbar fix (smoothed)
// -----------------------------------------------------------------------------
// Sets a CSS variable --vh based on the *actual* visible viewport height.
// In CSS, .drawer__inner should use:
//   height: calc(var(--vh, 1vh) * 100);
//   max-height: calc(var(--vh, 1vh) * 100);
// -----------------------------------------------------------------------------

(function () {
  let vhInitialized = false;
  let resizeTimeout = null;

  function setRealVh() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  function scheduleVhUpdate() {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(setRealVh, 150); 
  }

  window.__ensureCartDrawerVh = function () {
    if (vhInitialized) return;
    vhInitialized = true;

    setRealVh();
    window.addEventListener('resize', scheduleVhUpdate);
    window.addEventListener('orientationchange', scheduleVhUpdate);
  };
})();

// -----------------------------------------------------------------------------
// CartDrawer
// -----------------------------------------------------------------------------

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (window.__ensureCartDrawerVh) {
      window.__ensureCartDrawerVh();
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer') || document.getElementById('CartDrawer') || this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    // Only add overflow-hidden if this is not the popup cart drawer
    if (!this.classList.contains('cart-drawer-popup')) {
      document.body.classList.add('overflow-hidden');
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    if (!this.classList.contains('cart-drawer-popup')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.classList.toggle('is-empty', parsedState.item_count === 0);

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElements = section.selector
        ? document.querySelectorAll(section.selector)
        : document.querySelectorAll(`#${section.id}`);

      if (!sectionElements.length) {
        if (section.id === 'cart-icon-bubble') {
          const bubbleContainer = document.querySelector('#cart-icon-bubble-container');
          if (bubbleContainer) {
            const bubbleHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
            if (bubbleHTML) {
              bubbleContainer.innerHTML = bubbleHTML;
            } else {
              bubbleContainer.innerHTML = '';
            }
          }
        }
        return;
      }

      if (section.id === 'cart-icon-qty' && parsedState.item_count > 0) {
        const qtyElement = document.getElementById('cart-icon-qty');
        if (qtyElement) {
          const qtyHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (qtyHTML) {
            qtyElement.innerHTML = qtyHTML;
          }
        }
      }

      sectionElements.forEach((el) => {
        el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    });

    // Update qty / bubble in header
    if (parsedState.item_count > 0) {
      const qtyElement = document.getElementById('cart-icon-qty');
      if (qtyElement) {
        qtyElement.innerHTML = `<span class="cart-count-spacer">&nbsp;</span>(${parsedState.item_count}) `;
      } else {
        const cartButton = document.querySelector('a.header__icon--cart');
        if (cartButton) {
          const currentContent = cartButton.innerHTML;
          const newContent = currentContent.replace(
            'CART',
            `CART <span id="cart-icon-qty"><span class="cart-count-spacer">&nbsp;</span>(${parsedState.item_count}) </span>`
          );
          cartButton.innerHTML = newContent;
        }
      }
    } else if (parsedState.item_count === 0) {
      const qtyElement = document.getElementById('cart-icon-qty');
      if (qtyElement) {
        qtyElement.remove();
      }

      const bubbleContainer = document.querySelector('#cart-icon-bubble-container');
      if (bubbleContainer) {
        bubbleContainer.innerHTML = '';
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-qty',
        section: 'cart-icon-qty',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// -----------------------------------------------------------------------------
// CartDrawerItems
// -----------------------------------------------------------------------------

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-qty',
        section: 'cart-icon-qty',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
